import { useEffect, useRef, useState } from "react";
import { Bell, Check, CheckCheck } from "lucide-react";
import { useNavigate } from "react-router";

import { fmtDate } from "@/shared/lib/formatters";
import { playNotificationSound } from "../lib/notificationSound";

import {
  useContarNotificacoesNaoLidasQuery,
  useListarNotificacoesQuery,
  useMarcarNotificacaoLidaMutation,
  useMarcarTodasNotificacoesLidasMutation,
  type NotificacaoItem,
} from "../services/notificacaoApi";

// 15 segundos: equilíbrio entre "quase tempo real" e custo de back (4 req/min/user).
// Evolução futura → SignalR para push verdadeiro.
const POLL_INTERVAL_MS = 15_000;

export function NotificationBellDropdown() {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data: contador } = useContarNotificacoesNaoLidasQuery(undefined, {
    pollingInterval: POLL_INTERVAL_MS,
  });

  const { data: lista, refetch: refetchLista } = useListarNotificacoesQuery(20, {
    skip: !open,
  });

  const [marcarLida] = useMarcarNotificacaoLidaMutation();
  const [marcarTodasLidas] = useMarcarTodasNotificacoesLidasMutation();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const total = contador?.total ?? 0;

  // Toca som ao detectar aumento no contador (nova notificação chegou via polling).
  // `previousTotal === null` evita som no primeiro carregamento da sessão.
  const previousTotalRef = useRef<number | null>(null);
  useEffect(() => {
    if (contador === undefined) return;
    const previous = previousTotalRef.current;
    if (previous !== null && total > previous) {
      playNotificationSound();
    }
    previousTotalRef.current = total;
  }, [total, contador]);

  const handleItemClick = async (item: NotificacaoItem) => {
    setOpen(false);
    if (!item.lida) {
      try {
        await marcarLida(item.id).unwrap();
      } catch {
        // silencioso — melhor navegar do que bloquear
      }
    }
    if (item.linkAcao) {
      navigate(item.linkAcao);
    }
  };

  const handleMarcarTodas = async () => {
    try {
      await marcarTodasLidas().unwrap();
      refetchLista();
    } catch {
      // silencioso
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        aria-label="Notificações"
        onClick={() => setOpen((o) => !o)}
        className="relative flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100"
      >
        <Bell size={17} />
        {total > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#B32E34] px-1 text-[10px] font-semibold text-white">
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-[9000] mt-2 w-[360px] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <div>
              <h3 className="text-[14px] font-semibold text-gray-800">
                Notificações
              </h3>
              {total > 0 && (
                <p className="text-[11px] text-gray-500">
                  {total} não lida{total !== 1 ? "s" : ""}
                </p>
              )}
            </div>
            {total > 0 && (
              <button
                type="button"
                onClick={handleMarcarTodas}
                className="flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium text-[#034065] transition-colors hover:bg-[#034065]/5"
              >
                <CheckCheck size={12} />
                Marcar todas como lidas
              </button>
            )}
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {!lista ? (
              <div className="px-4 py-6 text-center text-[12px] text-gray-400">
                Carregando...
              </div>
            ) : lista.itens.length === 0 ? (
              <div className="px-4 py-8 text-center text-[13px] text-gray-500">
                Nenhuma notificação.
              </div>
            ) : (
              lista.itens.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleItemClick(item)}
                  className={`flex w-full items-start gap-3 border-b border-gray-50 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-gray-50 ${
                    item.lida ? "" : "bg-[#034065]/5"
                  }`}
                >
                  <div
                    className={`mt-1 h-2 w-2 flex-shrink-0 rounded-full ${
                      item.lida ? "bg-transparent" : "bg-[#034065]"
                    }`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="truncate text-[13px] font-medium text-gray-800">
                        {item.titulo}
                      </p>
                      {item.lida && (
                        <Check
                          size={12}
                          className="flex-shrink-0 text-gray-300"
                        />
                      )}
                    </div>
                    <p className="mt-0.5 line-clamp-2 text-[12px] text-gray-600">
                      {item.mensagem}
                    </p>
                    <p className="mt-1 text-[11px] text-gray-400">
                      {fmtDate(item.dataCadastro)}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
